/**
 * Third-person combat camera — the Dynasty Warriors rig.
 *
 * Reference plate section G: 6–9 m behind, 2.5–3.5 m up, 50–60° FOV, snappy but
 * damped, pulls back as the crowd thickens, orbits and vignettes on a musou,
 * never clips the ground or a fort wall.
 *
 * ── Framing, and why the default pose is what it is ────────────────────────
 * The boom is solved exactly the way the original scaffold solved it, because
 * ~40 capture scenarios pose the camera through `camrig.height` +
 * `__DW.camera(yaw, pitch, dist)` and must keep their framing to the pixel:
 *
 *     horiz = cos(pitch) * dist        px = focus.x - sin(yaw) * horiz
 *                                      pz = focus.z - cos(yaw) * horiz
 *     py    = focus.y + height - sin(pitch) * dist        camera.lookAt(focus)
 *
 * The scaffold's default was `height 2.55, pitch -0.07, dist 8.4`, which lands
 * the camera 4.59 m above the hero looking **20.5° down**: an RTS view with a
 * measured **0.00 % sky in frame** (docs/REQUESTS.md, logged by post). The fix
 * is to move the lift out of `height` and into the boom: with `height = 0` the
 * `pitch` term *is* the true look-down angle, so a small negative `pitch` tilts
 * the rig without lifting it and the sky comes back. Every scenario that sets
 * `height` explicitly is untouched by the change.
 *
 * What `DEF` resolves to. These are solved from the boom geometry and were
 * checked against the camera critic's own 60 Hz trace of the PREVIOUS pose,
 * which the same model reproduces to within 0.3 % on every field it measured:
 *
 *                       old rig        now       reference plate
 *     camera up          2.65 m       2.65 m     2.5-3.5 m   (G)
 *     camera back        6.69 m       6.11 m     6-9 m       (G)
 *     look-down           3.0°        12.0°      ~10-14°     (DW)
 *     hero, frame height 26.3 %       28.6 %     28-38 %     (B)
 *     hero head, row     56.6 %       42.2 %     ~43 %       (DW)
 *     horizon, row       44.9 %       29.1 %     25-40 %     (A)
 *
 * The single change that buys all of it is `focusY`. It used to aim 2.30 m up —
 * 0.45 m ABOVE the hero's head — which forces a near-level boom to keep him low
 * in frame, and a near-level boom is a sight line that runs THROUGH a crowd at
 * eye height with every grunt nearer the lens rendering larger than the hero.
 * Aiming at his chest (1.35 m) lets the rig sit at a real 12° and puts his head
 * where DW puts it, two fifths down the frame.
 *
 * And it breathes with the fight rather than sitting still: the boom measures
 * 6.25 m idle, pulls IN to 5.85 m as the ring closes (steepening to 15°, hero
 * 31.6 % of frame height) and eases out to 7.15 m with a 4° wider lens on a
 * musou, where the hero still reads at 23.7 %.
 *
 * `CFG.camera.{dist,height,pitch}` describe the OLD parameterisation and are
 * left alone (core file, shared ownership) — see docs/REQUESTS.md.
 *
 * ── Composition rules ─────────────────────────────────────────────────────
 * `rig.yaw/pitch/dist/height/fov` are the *base* pose and are never written by
 * the rig itself, so an external write (a scenario, the debug harness) is
 * authoritative: it is detected, snapped to without damping, and it switches
 * `autoFrame` off so crowd framing and boom collision cannot move a hand-posed
 * shot. Everything the rig adds — crowd ease-back, mode offsets, cinematics,
 * kickback, shake — is an offset applied on top, so nothing fights anything.
 *
 * Everything that must survive hitstop (shake, kickback, FOV pulse, orbit,
 * cinematic timing) runs on `realDt`; the follow damping runs on `dt`, so a
 * hitstop genuinely freezes the frame while the punch keeps playing.
 */
import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { ST, TEAM } from '../core/entities.js';
import { damp, clamp, clamp01, wrapPi, angleDamp, smoothstep, TAU } from '../core/mathx.js';
import { terrainSweep, fortSweep, propSweep } from './collide.js';
import { WALLS, sweep as wallSweep } from '../world/walls.js';
import { CINEMATICS, easeFn, poseField } from './cinematic.js';

/* ------------------------------------------------------------- tunables -- */

/** Default gameplay pose. See the header for the derivation. */
const DEF = {
  dist: 6.25,         // boom length (m) → 6.11 m of ground distance
  height: 0.0,        // extra lift above the pivot; 0 makes `pitch` the true pitch
  pitch: -0.2094,     // rad → the camera looks 12.0° down
  fov: 54,            // reference plate G says 50–60; every posed scenario inherits this
  focusY: 1.35,       // gameplay aim point above the hero's feet: his CHEST, see below
  lead: 1.0,          // velocity look-ahead scale
};

/**
 * The aim height used whenever the camera is HAND-POSED (`autoFrame` off).
 * The scaffold aimed at 1.45 m and every existing capture scenario was framed
 * against that, so posed shots keep it to the centimetre. Gameplay now aims at
 * 1.35 m — the hero's chest — which is within a centimetre of the same thing,
 * so posed and live framing finally agree.
 */
const LEGACY_FOCUS_Y = 1.45;

/**
 * Crowd framing. The rig used to answer a thickening ring by LENGTHENING the
 * boom 2.2 m while adding 2.6° of tilt, which walks the sight line further and
 * further THROUGH the ring: measured at 29–54 foes the lens sat 8.90 m back,
 * 3.46 m up and 5.54° down, the hero fell to 19.0 % of frame height, and in
 * four of twenty frames he was completely behind two enemy backs.
 *
 * It does the opposite now. Density pulls the boom IN 0.90 m, steepens the
 * tilt 4.0° to a 16.0° look-down, drops the aim 0.10 m and narrows the lens
 * 1.5°, which puts it 2.72 m up / 5.14 m back with the hero at **34.5 %** of
 * frame height instead of 19.0 % — bigger than the grunts in front of him
 * rather than a third their size. `hi` came down 26 → 16 as well: a real
 * brawl is ten to sixteen men inside 9 m, and at the old threshold the term
 * only ever reached a third of its travel in the frames that needed it.
 *
 * Two things are deliberately traded away. Steepening costs sky (the geometric
 * horizon goes 29 % → 21 % of frame height), and 5.14 m is under the plate's
 * 6–9 m stand-off. Both are right: when the ring closes, the frame's subject
 * is the ring rather than the weather, every reference frame of a hero inside
 * a peon ring has the lens closer than its own open-field default, and every
 * metre taken out of the boom is roughly one fewer RANK of men standing in the
 * gap between the lens and the man you are supposed to be watching.
 */
const CROWD = {
  radius: 9.0, lo: 3, hi: 16,
  dist: -0.90, pitch: -0.070, focusY: -0.10, fov: -1.5,
  lambda: 2.0,
};

/** Per-mode offsets applied on top of the base pose, blended in and out. */
const MODES = {
  // The musou pull-out used to add 3.1 m of boom on top of the crowd's 2.2 m,
  // which put the hero at ~16 % of frame height — invisible inside his own
  // finisher. It adds 1.5 m now and steepens 2.5°, so the ring reads without
  // losing the man in the middle. `height` is 0 so `pitch` stays the true tilt.
  musou:   { dist: 1.3, pitch: 0, height: 0, fov: 4.0, focusY: 0.10, orbit: 0.60, vignette: 0.62, lambda: 4.2 },
  duelcam: { dist: 1.1, pitch: -0.010, height: 0.15, fov: 1.0, focusY: 0.12, orbit: 0.00, vignette: 0,    lambda: 3.0 },
  // The finisher hold is a cinematic, so it drives the pose itself; this entry
  // exists only to hold the musou vignette up while the hold plays.
  musouHold: { dist: 0, pitch: 0, height: 0, fov: 0, focusY: 0, orbit: 0, vignette: 0.62, lambda: 6.0 },
  /**
   * Warrior select. A portrait boom: in to 4.4 m, up off the 12 deg gameplay
   * look-down to 6 deg, and a 42 deg lens so the hero compresses against the
   * valley instead of being drawn in gameplay's wide-angle.
   *
   * The tilt is the part that took a capture to get right. At gameplay's 12
   * deg look-down the horizon sat at 20 % of frame height and the warrior
   * stood against four fifths of empty dirt road. Cancelling almost all of it
   * (a net 1.4 deg down) puts the horizon across the middle and his head above
   * it, so he reads against sky the way a character-select portrait should.
   * `focusY` then aims at 1.20 m rather than the 1.35 m gameplay chest: at
   * this boom and lens the frame covers 3.4 m, and the lower aim gives the
   * feet somewhere to stand instead of cropping them.
   *
   * `orbit` is 0 on purpose: the mode orbit runs an envelope that decays to a
   * third of its rate after a few seconds (see the note at the orbit block),
   * which is right for a musou and wrong for a turntable that has to hold a
   * constant speed for as long as the player browses. select uses the
   * persistent `camrig.orbit(rate)` instead.
   */
  select:  { dist: -1.85, pitch: 0.185, height: 0, fov: -12, focusY: -0.15, orbit: 0, vignette: 0.52, lambda: 2.6 },
};

const PITCH_MIN = -0.92, PITCH_MAX = 0.55;
const DIST_MIN = 0.6, DIST_MAX = 420;
const GROUND_CLEAR = 1.0;      // hard floor above the terrain (scaffold parity)
const BOOM_CLEAR = 0.55;       // clearance kept when the boom is pulled in
const SHAKE_TRANS = 0.17;      // metres of camera offset per unit of shake amp
const SHAKE_ROLL = 0.011;      // radians of roll per unit of shake amp
/**
 * The impact springs, tuned so the punch ENDS WITH THE SHAKE.
 *
 * The shake envelope is 11 frames — 0.183 s, inside plate D's 0.12-0.22 s —
 * and it is armed once per sweep however many bodies the sweep catches. These
 * springs were not: at K 150 / C 19 the kick had a damping ratio of 0.78 and
 * a 4/(zeta*w0) settling time of 0.42 s, and the FOV pulse's own docstring
 * said "springs back over ~0.4 s". Worse, `kickback()` injects a VELOCITY
 * impulse, so the camera starts at rest and reaches peak displacement a
 * quarter period after the blow: measured on a C4, the camera's fastest
 * motion — 87.7 mm/frame, 5.3 m/s — arrived AFTER the shake had closed, and
 * the whole camera was still drifting 0.37-0.95 s per hit. Plate D wants
 * "2-4 oscillations then gone"; a long wobbly camera reads as cheap, and it
 * does not matter to the player which of three systems is producing it.
 *
 * K 480 / C 40 gives zeta 0.91 and settles in 0.20 s, next to the shake's
 * 0.183. A stiffer spring reaches a smaller peak for the same impulse, so the
 * input velocities are scaled up to land the same punch — and the scale is
 * MEASURED, not derived. The closed form for a velocity impulse says 0.78x
 * and a 1.28 compensation; a matched A/B on `stats.kick` said the peak still
 * fell from 66.2 mm to 40.5 mm on a C4, because `kick.d` is clamped against
 * the boom (KICK_BOOM_MIN, kdMax) while `kick.x/z` are not, and the closed
 * form knows nothing about that clamp. 1.63 more puts the peak back.
 *
 * The FOV pulse needs no such compensation: `pulseFov` adds to `fovp.v`, the
 * DISPLACEMENT, so stiffening only shortens the return.
 */
const KICK_V = 5.05;            // m/s of lateral kick per unit of kickback amount
const KICK_DOLLY = 7.1;       // m/s of dolly punch per unit
const KICK_K = 480, KICK_C = 40;
const FOV_K = 300, FOV_C = 32;
/**
 * Ceiling on the impact springs' velocity. `impact.js` calls `kickback()` once
 * per body, so a musou sweep that catches two dozen men used to inject two
 * dozen impulses into the same spring inside one frame. This is the same
 * ceiling it always was in METRES of dolly — ~0.32 m, the whole punch a
 * single heavy blow is worth — carried across the stiffer spring by the same
 * 1.28 the impulses were; anything past that is a pile-up, not a hit.
 */
const KICK_VMAX = 18.7;
/** The boom may never be pulled shorter than this by the dolly punch. */
const KICK_BOOM_MIN = 0.9;
/** Sub-step the impact springs at 60 Hz, at most this many times a frame. */
const SPRING_SUB_MAX = 8;
const LOCK_MAX = 30;           // lock-on drops beyond this range
const LOCK_FRAME_MAX = 7;      // beyond this separation the rig stops framing both
const LOCK_SHIFT_MAX = 2.2;    // metres the aim point may slide toward the target

/* -------------------------------------------------------------- scratch -- */

const _pos = new THREE.Vector3();
const _look = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _proj = new THREE.Vector3();
const _fortState = { frac: 1, frame: -99 };
/** Bodies found sitting on the lens→hero sight line. See `refreshOccluders`. */
const _occ = new Int32Array(24);

/* ========================================================================== */

export function createCameraRig() {
  /* solved pose — never the same object as the public base pose */
  const cur = {
    yaw: 0, pitch: DEF.pitch, dist: DEF.dist, height: DEF.height, fov: DEF.fov,
    fx: 0, fy: DEF.focusY, fz: 0, focusY: DEF.focusY, lead: DEF.lead,
  };
  const tgt = { yaw: 0, pitch: DEF.pitch, dist: DEF.dist, height: DEF.height, fov: DEF.fov, focusY: DEF.focusY, lead: DEF.lead };
  const seen = { yaw: 0, pitch: DEF.pitch, dist: DEF.dist, height: DEF.height, fov: DEF.fov };

  const sk = { amp: 0, freq: 22, t: 0, dur: 1, dx: 0, dz: 0, dirAmp: 0, ph: 0, gap: 9, rep: 0 };
  const kick = { x: 0, z: 0, vx: 0, vz: 0, d: 0, vd: 0 };
  const fovp = { v: 0, dv: 0 };
  const lock = { id: -1, sep: 0, bias: 0, weight: 0, mirrored: -1 };
  const crowd = { n: 0, raw: 0, t: 0, probeT: 0 };
  const pullS = { v: 1 };
  const mode = { blend: 0, vigBase: -1, vigTarget: -1, vigOn: false, t: 0 };
  const cine = { name: null, t: 0, dur: 0, def: null, yaw0: 0, prev: 'follow', from: null, orbit: 0 };
  const orbitS = { rate: 0, t: 0, acc: 0 };
  const free = { x: 0, y: 8, z: 16, yaw: 0, pitch: -0.2, speed: 22, primed: false, viaFlag: false };
  const pending = { yaw: 0, pitch: 0, zoom: 0 };
  const focusOv = { on: false, x: 0, y: 0, z: 0, t: 0, w: 1 };

  let world = null, frame = 0, lastMissionState = null, prevMusouPhase = null;

  /* ------------------------------------------------------------- helpers */

  function snapAll() {
    cur.yaw = rig.yaw; cur.pitch = rig.pitch; cur.dist = rig.dist;
    cur.height = rig.height; cur.fov = rig.fov;
  }
  function storeSeen() {
    seen.yaw = rig.yaw; seen.pitch = rig.pitch; seen.dist = rig.dist;
    seen.height = rig.height; seen.fov = rig.fov;
  }
  /** External writes to the base pose are authoritative: snap, stop auto-framing. */
  function detectExternal() {
    let hit = false;
    if (rig.yaw !== seen.yaw) { cur.yaw = rig.yaw; orbitS.acc = 0; hit = true; }
    if (rig.pitch !== seen.pitch) { cur.pitch = rig.pitch; hit = true; }
    if (rig.dist !== seen.dist) { cur.dist = rig.dist; hit = true; }
    if (rig.height !== seen.height) { cur.height = rig.height; hit = true; }
    if (rig.fov !== seen.fov) { cur.fov = rig.fov; hit = true; }
    if (hit && !cine.name) rig.autoFrame = false;
    return hit;
  }

  /* -------------------------------------------------------- public API -- */

  const rig = {
    /* base pose — written by input, scenarios and the harness; read by everyone */
    yaw: 0, pitch: DEF.pitch, dist: DEF.dist, height: DEF.height, fov: DEF.fov,
    mode: 'follow',
    /** Crowd framing + boom collision. An external pose write clears it. */
    autoFrame: true,
    /** Lock-on target entity id, or -1. */
    target: -1,
    /** Reticle hook for the HUD. `active` is false when nothing is locked. */
    reticle: { active: false, id: -1, x: 0, y: 0, ndcX: 0, ndcY: 0, dist: 0, onScreen: false, radius: 26 },
    /** The camera this rig renders from (audio takes its listener from it). */
    camera: null,
    /** Gameplay aim height above the hero's feet. Ignored for posed shots. */
    focusY: DEF.focusY,
    /** Measured local density driving the crowd framing. */
    crowd,
    /**
     * Entity ids currently standing between the lens and the hero's chest,
     * nearest-first-ish, valid for `occluderCount` entries. Recomputed every
     * other frame. The rig cannot solve these itself — a chase camera low
     * enough to keep the sky cannot see over a man standing 1.5 m from its
     * subject, and pulling the boom in only brings MORE bodies past the lens —
     * so this is published for the crowd/chars renderers to fade or cull.
     * See docs/REQUESTS.md.
     */
    occluders: _occ,
    occluderCount: 0,
    /** Read-only solved pose, for debug overlays and captures. */
    solved: cur,
    // `shake` is the shake envelope's amplitude; `kick` is the impact spring's
    // own offset in metres and `fovPulse` the lens pulse in degrees. All three
    // exist because a probe that reads the CAMERA'S POSITION cannot tell an
    // impact spring from the boom following a hero who is moving under root
    // motion, and a whole round was spent tuning the wrong system on exactly
    // that confusion. These read the systems themselves.
    stats: { boomPull: 1, crowdN: 0, lookDownDeg: 0, mode: 'follow', shake: 0, kick: 0, fovPulse: 0, occluders: 0,
             /* true while a scripted cinematic owns the pose: movement keeps its
                own basis then, so a flythrough cannot spin the controls */
             cinematic: false,
             /* write-out diagnostics: the springs and the boom length that
                actually reached `cam.position` this frame */
             kickD: 0, kickX: 0, kickZ: 0, fovPulse: 0, boomEff: 0, rdt: 0 },
    defaults: DEF,
    free,

    /* legacy scaffold fields other code may still read */
    get shakeAmp() { return sk.dur > 0 ? sk.amp * (sk.t / sk.dur) : 0; },
    get shakeT() { return sk.t; },
    get shakeFreq() { return sk.freq; },
    pos: _pos, look: _look,

    /**
     * Screen shake. `amp` is the peak camera offset in units of ~0.17 m,
     * `freq` in Hz, `dur` in seconds. Optional `dirX/dirZ` bias it along the
     * world direction of the blow (DW's directional punch).
     *
     * Composes by MAX, never by sum: impact.js calls this once per body, and a
     * 24-body musou sweep must not stack into a seizure.
     */
    shake(a, f = 22, d = 0.25, dirX = 0, dirZ = 0) {
      if (!(a > 0)) return;
      // A SUSTAINED stream must not re-arm the peak on every call. The musou
      // grinder pulses at 8 Hz, faster than a 0.20 s envelope can fall, so the
      // old code re-triggered a fresh full-amplitude shake ~30 times in a row
      // and `stats.shake` measured a flat 0.325–0.445 for the whole 3.8 s.
      // Reference plate D asks for 2–4 oscillations and then gone, so a burst
      // fades toward a quarter of one blow's worth of rumble and stays there.
      if (sk.t > 0 && sk.gap < 0.30) sk.rep++; else sk.rep = 0;
      sk.gap = 0;
      if (sk.rep > 0) { const fade = 1 / (1 + sk.rep * 0.22); a *= fade < 0.25 ? 0.25 : fade; }
      const remain = sk.dur > 0 ? sk.amp * (sk.t / sk.dur) : 0;
      if (a >= remain) { sk.amp = a; sk.freq = f; sk.t = d; sk.dur = Math.max(0.01, d); }
      else sk.t = Math.min(sk.dur, Math.max(sk.t, d * 0.6));
      const l = Math.hypot(dirX, dirZ);
      if (l > 1e-4) { sk.dx = dirX / l; sk.dz = dirZ / l; sk.dirAmp = 1; }
    },

    /**
     * Impact kickback along the blow direction plus a dolly punch toward the
     * hero. Heavy blows (amount >= 0.5) also pulse the lens.
     * Called by combat.js once per connecting sweep.
     */
    kickback(amount = 0.3, dirX = 0, dirZ = 0) {
      const a = clamp(amount, 0, 2.5);
      if (!(a > 0)) return;
      const l = Math.hypot(dirX, dirZ);
      if (l > 1e-4) { kick.vx += (dirX / l) * a * KICK_V; kick.vz += (dirZ / l) * a * KICK_V; }
      kick.vd += a * KICK_DOLLY;
      // One sweep, one punch: clamp the accumulation so 24 bodies in a frame
      // cannot stack 24 impulses into the same spring.
      const lv = Math.hypot(kick.vx, kick.vz);
      if (lv > KICK_VMAX) { const s = KICK_VMAX / lv; kick.vx *= s; kick.vz *= s; }
      kick.vd = clamp(kick.vd, -KICK_VMAX, KICK_VMAX);
      if (a >= 0.5) rig.pulseFov(-(0.9 + 2.4 * (a - 0.5)));
    },

    /** Instant FOV kick in degrees; springs back over ~0.25 s. */
    pulseFov(deg) { fovp.v += deg; return fovp.v; },

    /**
     * Aim at a world point instead of the hero. `focus()` clears the override.
     * `weight` < 1 blends between the hero and the point.
     */
    focus(x, y, z, seconds = 0, weight = 1) {
      if (x === undefined || x === null) { focusOv.on = false; focusOv.t = 0; return; }
      focusOv.on = true; focusOv.x = x; focusOv.y = y; focusOv.z = z;
      focusOv.t = seconds > 0 ? seconds : Infinity; focusOv.w = clamp01(weight);
    },

    /**
     * 'follow' | 'musou' | 'free' | 'duelcam' | any string. Unknown names are
     * stored verbatim so nothing that already calls this breaks; only the
     * names in MODES change the framing.
     */
    setMode(name) {
      const n = name || 'follow';
      if (n === rig.mode) return rig.mode;
      if (cine.name === 'musouHold') endCinematic(false);
      if (cine.name && n !== cine.name) endCinematic(n === 'follow');
      rig.mode = n;
      mode.t = 0;                      // restart the orbit ease (see below)
      if (n === 'free') { primeFree(); }
      return rig.mode;
    },

    /**
     * Soft lock-on.
     *   lockOn()        toggle — acquire the best target ahead, or drop it
     *   lockOn(id)      lock that entity
     *   lockOn(-1|null) drop
     * Returns the current target id.
     */
    lockOn(id) {
      if (id === undefined) {
        const P = world?.player;
        if (P?.toggleLock) setGameplayLock(P.toggleLock());
        else if (lock.id >= 0) setLock(-1);
        else setLock(acquire(world, 24));
      } else setGameplayLock(id === null ? -1 : id | 0);
      return rig.target;
    },

    /** Switch to the next lock target to the left (-1) or right (+1). */
    switchTarget(dir = 1) { setGameplayLock(nextTarget(world, dir >= 0 ? 1 : -1)); return rig.target; },

    /**
     * Manual orbit. `orbit(rate)` sets a persistent yaw rate (rad/s),
     * `orbit(rate, seconds)` runs it for a while, `orbit(0)` stops.
     */
    orbit(rate = 0, seconds = 0) {
      orbitS.rate = rate;
      orbitS.t = rate ? (seconds > 0 ? seconds : Infinity) : 0;
      return orbitS.rate;
    },

    /**
     * Run a scripted move: 'intro' | 'musouHold' | 'death' | 'victory' |
     * 'duel'. `cinematic(null)` cancels back to the follow rig.
     * Returns the duration in seconds (0 if the name is unknown).
     */
    cinematic(name, opts) {
      if (!name) { endCinematic(true); return 0; }
      const def = CINEMATICS[name];
      if (!def) return 0;
      cine.name = name; cine.def = def; cine.t = 0;
      cine.dur = (opts && opts.seconds) || def.dur;
      cine.prev = MODES[rig.mode] ? rig.mode : 'follow';
      cine.yaw0 = cur.yaw - orbitS.acc;
      cine.orbit = def.orbit || 0;
      if (def.from) {
        cine.from = def.from;
        cur.pitch = def.from.pitch; cur.dist = def.from.dist;
        cur.height = def.from.height; cur.fov = def.from.fov;
        cur.yaw = cine.yaw0 + def.from.yawRel;
      } else cine.from = captureFrom();
      rig.mode = name;
      return cine.dur;
    },

    /** Place and aim the free/debug camera; switches to free mode. */
    fly(x, y, z, yaw, pitch) {
      free.x = x; free.y = y; free.z = z;
      if (yaw !== undefined) free.yaw = yaw;
      if (pitch !== undefined) free.pitch = pitch;
      free.primed = true;
      if (world) world.flags.freeCam = true;
      rig.setMode('free');
      return free;
    },
    /** Aim the free camera at a world point. */
    freeLook(tx, ty, tz) {
      const dx = tx - free.x, dy = ty - free.y, dz = tz - free.z;
      free.yaw = Math.atan2(dx, dz);
      free.pitch = Math.atan2(dy, Math.hypot(dx, dz));
      return free;
    },

    /** Drop every transient and return to the default gameplay pose. */
    reset() {
      sk.amp = 0; sk.t = 0; sk.dirAmp = 0; sk.rep = 0; sk.gap = 9;
      kick.x = kick.z = kick.vx = kick.vz = kick.d = kick.vd = 0;
      fovp.v = 0; fovp.dv = 0;
      world?.player?.setLock?.(-1);
      setLock(-1); lock.mirrored = -1; endCinematic(true);
      orbitS.rate = 0; orbitS.t = 0; orbitS.acc = 0;
      crowd.t = 0; mode.blend = 0;
      rig.yaw = 0; rig.pitch = DEF.pitch; rig.dist = DEF.dist;
      rig.height = DEF.height; rig.fov = DEF.fov; rig.focusY = DEF.focusY;
      rig.autoFrame = true; rig.mode = 'follow';
      if (world) world.flags.freeCam = false;
      snapAll(); storeSeen();
      return rig;
    },
    /** Jump the smoothed pose onto the base pose (use after a teleport). */
    snap() { snapAll(); storeSeen(); return rig; },

    /**
     * Set the base pose WITHOUT tripping the hand-posed detector, so crowd
     * framing and boom collision stay live. Use this to retune the gameplay
     * rig (`__DW.camera()` is the hand-pose path and deliberately disables
     * auto-framing instead).
     */
    setPose(o) {
      if (!o) return rig;
      if (o.yaw !== undefined) { rig.yaw = o.yaw; cur.yaw = o.yaw; }
      if (o.pitch !== undefined) { rig.pitch = o.pitch; cur.pitch = o.pitch; }
      if (o.dist !== undefined) { rig.dist = o.dist; cur.dist = o.dist; }
      if (o.height !== undefined) { rig.height = o.height; cur.height = o.height; }
      if (o.fov !== undefined) { rig.fov = o.fov; cur.fov = o.fov; }
      if (o.focusY !== undefined) rig.focusY = o.focusY;
      if (o.snap !== false) snapAll();
      storeSeen();
      return rig;
    },
  };

  /* ------------------------------------------------------ lock-on utils -- */

  function setLock(id) {
    const w = world;
    if (!w || id === undefined || id === null || id < 0) {
      lock.id = -1; rig.target = -1; rig.reticle.active = false; rig.reticle.id = -1; return -1;
    }
    const e = w.entities;
    if (!e.alive[id] || e.state[id] === ST.DEAD || id === w.playerId) { lock.id = -1; rig.target = -1; return -1; }
    lock.id = id; rig.target = id;
    return id;
  }

  // Gameplay owns target choice; the camera retains only a presentation cache
  // for framing and the reticle. Programmatic camera hooks therefore delegate
  // through the player API and mirror the accepted target immediately.
  function setGameplayLock(id) {
    const P = world?.player;
    const accepted = P?.setLock ? P.setLock(id) : id;
    const target = setLock(accepted);
    lock.mirrored = target;
    return target;
  }

  /** Best target in front of the camera within `r` metres. */
  function acquire(w, r) {
    if (!w || w.playerId < 0) return -1;
    const e = w.entities, id = w.playerId;
    const px = e.x[id], pz = e.z[id], team = e.team[id];
    const q = w.scratch.ids;
    const n = e.around(px, pz, r, q, q.length);
    const fx = Math.sin(cur.yaw), fz = Math.cos(cur.yaw);
    let best = -1, bs = -1e9;
    for (let i = 0; i < n; i++) {
      const t = q[i];
      if (t === id || !e.alive[t] || e.state[t] === ST.DEAD) continue;
      if (e.team[t] === team || e.team[t] === TEAM.NEUTRAL) continue;
      const dx = e.x[t] - px, dz = e.z[t] - pz;
      const d = Math.hypot(dx, dz);
      if (d > r || d < 0.2) continue;
      const dot = (dx / d) * fx + (dz / d) * fz;
      if (dot < 0.2) continue;
      const s = dot * 2.2 - d * 0.08 + (e.kind[t] >= 2 && e.kind[t] <= 4 ? 1.4 : 0);
      if (s > bs) { bs = s; best = t; }
    }
    return best;
  }

  /** Next target to the left/right of the current one, in screen space. */
  function nextTarget(w, dir) {
    if (!w || w.playerId < 0) return -1;
    const e = w.entities, id = w.playerId, cam = w.camera;
    const px = e.x[id], pz = e.z[id], team = e.team[id];
    const q = w.scratch.ids;
    const n = e.around(px, pz, LOCK_MAX, q, q.length);
    cam.updateMatrixWorld(true);
    cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
    let curX = -dir * 2;
    if (lock.id >= 0 && e.alive[lock.id]) {
      _proj.set(e.x[lock.id], e.y[lock.id] + 1.1, e.z[lock.id]).project(cam);
      curX = _proj.x;
    }
    let best = -1, bs = 1e9;
    for (let i = 0; i < n; i++) {
      const t = q[i];
      if (t === id || t === lock.id || !e.alive[t] || e.state[t] === ST.DEAD) continue;
      if (e.team[t] === team || e.team[t] === TEAM.NEUTRAL) continue;
      _proj.set(e.x[t], e.y[t] + 1.1, e.z[t]).project(cam);
      if (_proj.z > 1 || Math.abs(_proj.x) > 1.25 || Math.abs(_proj.y) > 1.25) continue;
      const d = (_proj.x - curX) * dir;
      if (d <= 0.01) continue;
      if (d < bs) { bs = d; best = t; }
    }
    return best >= 0 ? best : lock.id;
  }

  /* ----------------------------------------------------------- cinematic */

  const _from = { yawRel: 0, pitch: 0, dist: 0, height: 0, fov: 0, focusY: 0, lead: 0 };
  function captureFrom() {
    _from.yawRel = cur.yaw - orbitS.acc - cine.yaw0;
    _from.pitch = cur.pitch; _from.dist = cur.dist; _from.height = cur.height;
    _from.fov = cur.fov; _from.focusY = cur.focusY; _from.lead = cur.lead;
    return _from;
  }

  function endCinematic(toFollow) {
    if (!cine.name) { if (toFollow) rig.mode = 'follow'; return; }
    const def = cine.def;
    const back = toFollow ? 'follow' : (def.exit === 'prev' ? cine.prev : def.exit || 'follow');
    // fold the accumulated orbit into the base yaw so nothing whips back
    rig.yaw = wrapPi(rig.yaw + orbitS.acc);
    cur.yaw = wrapPi(cur.yaw);
    orbitS.acc = 0;
    cine.name = null; cine.def = null; cine.orbit = 0;
    rig.mode = back || 'follow';
  }

  /* ------------------------------------------------------------ free cam */

  function primeFree() {
    if (free.primed || !world) return;
    const cam = world.camera;
    free.x = cam.position.x; free.y = cam.position.y; free.z = cam.position.z;
    free.yaw = cur.yaw; free.pitch = cur.pitch; free.primed = true;
  }

  function solveFree(w, realDt) {
    const cam = w.camera, inp = w.input;
    primeFree();
    free.yaw = wrapPi(free.yaw - pending.yaw);
    free.pitch = clamp(free.pitch - pending.pitch, -1.5, 1.5);
    pending.yaw = 0; pending.pitch = 0; pending.zoom = 0;
    if (inp && inp.held) {
      const spd = free.speed * (inp.held('sprint') ? 3.2 : 1) * realDt;
      const s = Math.sin(free.yaw), c = Math.cos(free.yaw);
      const mx = inp.move.x || 0, my = inp.move.y || 0;
      free.x += (mx * c - my * s) * spd;
      free.z += (mx * s + my * c) * spd;
      if (inp.held('special')) free.y += spd;
      if (inp.held('guard')) free.y -= spd;
    }
    cam.position.set(free.x, free.y, free.z);
    const cp = Math.cos(free.pitch);
    _look.set(free.x + Math.sin(free.yaw) * cp, free.y + Math.sin(free.pitch), free.z + Math.cos(free.yaw) * cp);
    cam.up.set(0, 1, 0);
    cam.lookAt(_look);
    if (Math.abs(cam.fov - rig.fov) > 1e-3) { cam.fov = rig.fov; cam.updateProjectionMatrix(); }
    _pos.copy(cam.position);
    rig.stats.mode = 'free';
  }

  /* ------------------------------------------------------- crowd density */

  function probeCrowd(w, dt) {
    crowd.probeT -= dt;
    if (crowd.probeT <= 0) {
      crowd.probeT = 0.12;
      const e = w.entities, id = w.playerId;
      const foe = e.team[id] === TEAM.PLAYER ? TEAM.ENEMY : TEAM.PLAYER;
      crowd.raw = e.countIn(e.x[id], e.z[id], CROWD.radius, foe);
      crowd.n = crowd.raw;
    }
    const want = smoothstep((crowd.raw - CROWD.lo) / (CROWD.hi - CROWD.lo));
    crowd.t = damp(crowd.t, want, CROWD.lambda, dt);
  }

  /**
   * Which live bodies sit on the lens→hero sight line. One `around()` query on
   * the segment's midpoint plus a 2D point-to-segment test per candidate, so
   * it is allocation-free and costs about what the crowd density probe does.
   */
  function refreshOccluders(w, cam) {
    const e = w.entities, id = w.playerId;
    let k = 0;
    const hx = e.x[id], hz = e.z[id], hy = e.y[id] + 0.95;
    const dx = cam.position.x - hx, dz = cam.position.z - hz;
    const l2 = dx * dx + dz * dz;
    if (l2 > 1e-4) {
      const q = w.scratch.ids;
      const n = e.around(hx + dx * 0.5, hz + dz * 0.5, Math.sqrt(l2) * 0.5 + 1.6, q, q.length);
      const dy = cam.position.y - hy;
      for (let i = 0; i < n && k < _occ.length; i++) {
        const t = q[i];
        if (t === id || !e.alive[t] || e.state[t] === ST.DEAD) continue;
        const u = ((e.x[t] - hx) * dx + (e.z[t] - hz) * dz) / l2;
        if (u < 0.06 || u > 0.94) continue;               // not between them
        const ox2 = hx + dx * u - e.x[t], oz2 = hz + dz * u - e.z[t];
        const rad = e.radius[t] > 0.05 ? e.radius[t] : 0.35;
        if (ox2 * ox2 + oz2 * oz2 > rad * rad) continue;  // not on the line
        const top = e.y[t] + (e.height[t] > 0.1 ? e.height[t] : 1.8) * (e.scale[t] || 1);
        if (hy + dy * u > top) continue;                  // the line clears him
        _occ[k++] = t;
      }
    }
    rig.occluderCount = k;
    rig.stats.occluders = k;
  }

  /* ============================================================ system == */

  return {
    name: 'camera', order: ORDER.CAMERA,

    init(w) {
      world = w;
      rig.camera = w.camera;
      w.camrig = rig;
      snapAll(); storeSeen();

      w.bus.on('player:musou', (kind) => {
        if (kind === 'start') rig.setMode('musou');
        else if (kind === 'end') { if (cine.name === 'musouHold') endCinematic(true); rig.setMode('follow'); }
      });
      w.bus.on('kill', (victim) => { if (victim === w.playerId) rig.cinematic('death'); });
      // The opening establishing sweep is NOT run on boot: every capture
      // scenario would spend its first five seconds inside it. It waits for the
      // mission to say the battle has started (docs/REQUESTS.md).
      w.bus.on('mission:start', () => rig.cinematic('intro'));

      if (w.cfg?.debug?.freeCam) w.flags.freeCam = true;

      // Scenario hooks so captures can drive the rig without reaching inside.
      const dw = window.__DW;
      if (dw && dw.scenarios) {
        dw.scenarios.camCinematic = (wd, name) => wd.camrig.cinematic(name);
        dw.scenarios.camLock = (wd, id) => wd.camrig.lockOn(id);
        dw.scenarios.camMode = (wd, m) => wd.camrig.setMode(m);
        dw.scenarios.camReset = (wd) => { wd.camrig.reset(); return wd.camrig.mode; };
        dw.scenarios.camInfo = (wd) => ({ ...wd.camrig.stats, ...wd.camrig.solved });
        dw.scenarios.camPose = (wd, o) => { wd.camrig.setPose(o); return wd.camrig.mode; };
      }
    },

    /**
     * Input is drained here, not in lateUpdate: `input.lateUpdate` (ORDER.INPUT)
     * zeroes `look` before ORDER.CAMERA ever runs, which is why the scaffold's
     * mouse look never moved the camera.
     */
    update(w) {
      const inp = w.input;
      if (!inp) return;
      pending.yaw += (inp.look.x || 0) * 0.0026;
      pending.pitch += (inp.look.y || 0) * 0.0020;
      pending.zoom += (inp.look.z || 0) * 0.55;
    },

    lateUpdate(w, dt, alpha, realDt) {
      frame++;
      const cam = w.camera, e = w.entities, id = w.playerId;
      const rdt = realDt || dt || 1 / 60;
      rig.camera = cam;

      /* ---- free / debug camera ------------------------------------ */
      if (w.flags.freeCam) { if (rig.mode !== 'free') { rig.setMode('free'); free.viaFlag = true; } }
      else if (rig.mode === 'free' && free.viaFlag) { free.viaFlag = false; rig.setMode('follow'); }
      if (rig.mode === 'free') { solveFree(w, rdt); storeSeen(); return; }
      if (id < 0) return;

      /* ---- authoritative external pose writes --------------------- */
      detectExternal();
      const auto = rig.autoFrame;

      /* ---- mouse / stick look ------------------------------------- */
      if (!cine.name) {
        if (pending.yaw) rig.yaw = wrapPi(rig.yaw - pending.yaw);
        if (pending.pitch) rig.pitch = clamp(rig.pitch - pending.pitch, PITCH_MIN, PITCH_MAX);
        if (pending.zoom) rig.dist = clamp(rig.dist + pending.zoom, 3.0, 24);
      }
      pending.yaw = 0; pending.pitch = 0; pending.zoom = 0;

      /* ---- mission-driven cinematics ------------------------------ */
      const ms = w.mission ? w.mission.state : null;
      if (ms !== lastMissionState) {
        if (lastMissionState !== null) {
          if (ms === 'victory') rig.cinematic('victory');
          else if (ms === 'defeat') rig.cinematic('death');
        }
        lastMissionState = ms;
      }
      const ph = w.player ? w.player.musouPhase : null;
      if (ph === 'finish' && prevMusouPhase !== 'finish' && !cine.name) rig.cinematic('musouHold');
      prevMusouPhase = ph;

      /* ---- lock-on ------------------------------------------------- */
      const P = w.player;
      if (P) {
        if (P.locked && P.lockId >= 0 && lock.id !== P.lockId) { setLock(P.lockId); lock.mirrored = P.lockId; }
        else if (!P.locked && lock.mirrored >= 0 && lock.id === lock.mirrored) { setLock(-1); lock.mirrored = -1; }
      }
      if (lock.id >= 0) {
        const t = lock.id;
        if (!e.alive[t] || e.state[t] === ST.DEAD) setLock(-1);
        else {
          lock.sep = Math.hypot(e.x[t] - e.x[id], e.z[t] - e.z[id]);
          if (lock.sep > LOCK_MAX) setLock(-1);
        }
      }
      lock.weight = damp(lock.weight, lock.id >= 0 ? 1 : 0, 7, dt);

      /* ---- crowd framing ------------------------------------------ */
      if (auto && !cine.name) probeCrowd(w, dt);
      else crowd.t = damp(crowd.t, 0, 3.2, dt);

      /* ---- mode blend + vignette ---------------------------------- */
      const mcfg = MODES[rig.mode] || null;
      mode.blend = damp(mode.blend, mcfg ? 1 : 0, mcfg ? mcfg.lambda : 3.4, rdt);
      const mb = mode.blend;

      const pl = w.pipeline;
      const wantVig = mcfg && mcfg.vignette ? mcfg.vignette : 0;
      if (pl && pl.setVignette) {
        if (wantVig > 0) {
          if (!mode.vigOn) { mode.vigBase = pl.base ? pl.base.vignette : 0.28; mode.vigOn = true; }
          mode.vigTarget = wantVig;
        }
        if (mode.vigOn) {
          const b = mode.vigBase < 0 ? 0.28 : mode.vigBase;
          pl.setVignette(b + (Math.max(mode.vigTarget, b) - b) * mb);
          if (wantVig <= 0 && mb < 0.004) { pl.setVignette(b); mode.vigOn = false; }
        }
      }

      /* ---- orbit --------------------------------------------------- *
       * A mode orbit is EASED, not switched on. `mcfg.orbit * mb` alone gave a
       * dead-constant 34.0 deg/s from t=0.84 s to t=3.36 s of a musou with no
       * ease in, no ease out and no beat anywhere — a turntable, not a camera
       * move. The envelope below opens over 0.45 s, peaks at ~29 deg/s and
       * relaxes toward a third of that as the finisher runs, so the swing has
       * a shape; `mb` still closes it out when the mode ends.
       */
      if (mcfg) mode.t += rdt; else mode.t = 0;
      let orbitRate = 0;
      if (mcfg && mcfg.orbit) {
        const env = smoothstep(mode.t / 0.45) * (0.34 + 0.66 * Math.exp(-mode.t * 0.55));
        orbitRate += mcfg.orbit * mb * env;
      }
      if (cine.name && cine.orbit) orbitRate += cine.orbit;
      if (orbitS.t > 0) { orbitRate += orbitS.rate; orbitS.t -= rdt; if (orbitS.t <= 0) orbitS.rate = 0; }
      if (orbitRate) orbitS.acc = wrapPi(orbitS.acc + orbitRate * rdt);
      else if (!cine.name && !mcfg && orbitS.acc) {
        // The orbit is FOLDED into the base yaw, not unwound. Damping `acc`
        // back to zero rewinds the arc the musou just flew: measured over the
        // 180 frames after a musou ends, `solved.yaw` moved at up to
        // 240 deg/s against the 34 deg/s the orbit itself ran at — a seven-fold
        // whip back to where the camera started, which is not what any DW game
        // does and is the single worst thing left in this rig's motion.
        // `endCinematic` already folds for exactly this reason; the mode path
        // did not. Folding keeps `tgt.yaw = rig.yaw + orbitS.acc` numerically
        // identical, so `cur.yaw` does not move at all on the fold frame.
        rig.yaw = wrapPi(rig.yaw + orbitS.acc);
        orbitS.acc = 0;
      }

      /* ---- the pose we are solving toward ------------------------- */
      const ct = auto ? crowd.t : 0;
      tgt.pitch = clamp(rig.pitch + CROWD.pitch * ct + (mcfg ? mcfg.pitch * mb : 0), PITCH_MIN, PITCH_MAX);
      tgt.dist = clamp(rig.dist + CROWD.dist * ct + (mcfg ? mcfg.dist * mb : 0), DIST_MIN, DIST_MAX);
      tgt.height = rig.height + (mcfg ? mcfg.height * mb : 0);
      tgt.fov = rig.fov + CROWD.fov * ct + (mcfg ? mcfg.fov * mb : 0);
      tgt.focusY = (auto ? rig.focusY : LEGACY_FOCUS_Y) + CROWD.focusY * ct + (mcfg ? mcfg.focusY * mb : 0);
      tgt.lead = auto ? DEF.lead : 0;
      tgt.yaw = rig.yaw + orbitS.acc;

      /* ---- lock-on shaping: keep hero AND target in frame ---------- */
      let lockFx = 0, lockFz = 0;
      if (auto && lock.weight > 0.001 && lock.id >= 0) {
        const t = lock.id;
        const dx = e.x[t] - e.x[id], dz = e.z[t] - e.z[id];
        const sep = Math.max(0.001, Math.hypot(dx, dz));
        // Framing BOTH bodies is only affordable while they are close: two
        // points `sep` apart need roughly `sep / (2 tan(fov/2))` of camera
        // distance, so honouring a 12 m lock cost 2.8 m of extra boom and a
        // 4.1 m focus shift and left the hero a sliver at ~15 % of frame
        // height. Past LOCK_FRAME_MAX the rig stops trying: it keeps facing
        // the target and lets the HUD reticle carry it off-centre, which is
        // what DW's soft lock does.
        const sepF = sep > LOCK_FRAME_MAX ? LOCK_FRAME_MAX : sep;
        lock.bias = clamp(0.14 + sepF * 0.022, 0, 0.30) * lock.weight;
        const shift = Math.min(sep * lock.bias, LOCK_SHIFT_MAX);
        lockFx = (dx / sep) * shift; lockFz = (dz / sep) * shift;
        tgt.dist = clamp(tgt.dist + clamp(sepF * 0.24, 0, 1.7) * lock.weight, DIST_MIN, DIST_MAX);
        if (!cine.name) {
          rig.yaw = angleDamp(rig.yaw, Math.atan2(dx, dz), 5.5 * lock.weight, dt);
          tgt.yaw = rig.yaw + orbitS.acc;
        }
      }

      /* ---- cinematic override ------------------------------------- */
      if (cine.name) {
        cine.t += rdt;
        const def = cine.def;
        const p = easeFn(def.ease)(clamp01(cine.dur > 0 ? cine.t / cine.dur : 1));
        tgt.pitch = poseField(cine.from, def.to, rig.pitch, 'pitch', p);
        tgt.dist = poseField(cine.from, def.to, rig.dist, 'dist', p);
        tgt.height = poseField(cine.from, def.to, rig.height, 'height', p);
        tgt.fov = poseField(cine.from, def.to, rig.fov, 'fov', p);
        tgt.focusY = poseField(cine.from, def.to, rig.focusY, 'focusY', p);
        tgt.lead = poseField(cine.from, def.to, 0, 'lead', p);
        tgt.yaw = cine.yaw0 + poseField(cine.from, def.to, 0, 'yawRel', p) + orbitS.acc;
        if (cine.t >= cine.dur && !def.hold) endCinematic(false);
      }
      const cinematic = !!cine.name;

      /* ---- focus point -------------------------------------------- */
      let fx = e.x[id] + lockFx, fz = e.z[id] + lockFz, fy = e.y[id] + tgt.focusY;
      if (tgt.lead > 0.01) {
        const vx = e.vx[id], vz = e.vz[id];
        const sp2 = Math.hypot(vx, vz);
        if (sp2 > 0.6) {
          const k = Math.min(0.9, sp2 * 0.085) * tgt.lead;
          fx += (vx / sp2) * k; fz += (vz / sp2) * k;
        }
      }
      if (focusOv.on) {
        focusOv.t -= rdt;
        if (focusOv.t <= 0) focusOv.on = false;
        else { const wgt = focusOv.w; fx += (focusOv.x - fx) * wgt; fy += (focusOv.y - fy) * wgt; fz += (focusOv.z - fz) * wgt; }
      }

      // A teleport (harness, respawn) must not drag the boom across the map.
      if (frame < 3 || Math.abs(fx - cur.fx) + Math.abs(fz - cur.fz) + Math.abs(fy - cur.fy) > 6) {
        cur.fx = fx; cur.fy = fy; cur.fz = fz;
      } else {
        cur.fx = damp(cur.fx, fx, 13, dt);
        cur.fz = damp(cur.fz, fz, 13, dt);
        cur.fy = damp(cur.fy, fy, 7.5, dt);   // softer vertically: slopes must not bob the frame
      }

      /* ---- damp the pose ------------------------------------------ */
      const lam = cinematic ? 30 : 12;
      cur.yaw = cinematic ? tgt.yaw : angleDamp(cur.yaw, tgt.yaw, 14, dt);
      cur.pitch = damp(cur.pitch, tgt.pitch, lam, dt);
      cur.dist = damp(cur.dist, tgt.dist, lam * 0.85, dt);
      cur.height = damp(cur.height, tgt.height, lam * 0.85, dt);
      cur.fov = damp(cur.fov, tgt.fov, cinematic ? 20 : 9, dt);
      cur.focusY = tgt.focusY; cur.lead = tgt.lead;

      /* ---- solve the boom ----------------------------------------- */
      const cp = Math.cos(cur.pitch), sp = Math.sin(cur.pitch);
      const ox = Math.sin(cur.yaw) * cp, oz = Math.cos(cur.yaw) * cp;
      let dist = cur.dist;
      let px = cur.fx - ox * dist, pz = cur.fz - oz * dist;
      let py = cur.fy + cur.height - sp * dist;

      /* ---- occlusion: pull the boom IN (lifting would eat the sky) - */
      let pull = 1;
      if (auto && !cinematic) {
        const tf = terrainSweep(w.terrain, cur.fx, cur.fy, cur.fz, px, py, pz, BOOM_CLEAR);
        const ff = fortSweep(w, _fortState, frame, cur.fx, cur.fy, cur.fz, px, py, pz, BOOM_CLEAR, 2);
        const pf = propSweep(w, cur.fx, cur.fy, cur.fz, px, py, pz, BOOM_CLEAR);
        pull = tf < ff ? tf : ff;
        if (pf < pull) pull = pf;
        // the arena's walls are analytic boxes: a slab test each, no raycast
        if (WALLS.n) {
          const wf = wallSweep(cur.fx, cur.fy, cur.fz, px, py, pz, BOOM_CLEAR, w.terrain.heightAt);
          if (wf < pull) pull = wf;
        }
        // Snap IN, ease OUT. Coming in has to be instant — a frame spent
        // looking through a tree trunk or a fort wall is a frame lost — but
        // going back out on the same schedule makes the boom flick every time
        // a trunk clips the edge of the sweep, so the release is damped.
        if (pull < pullS.v) pullS.v = pull; else pullS.v = damp(pullS.v, pull, 3.2, rdt);
        pull = pullS.v;
        if (pull < 1) {
          pull = pull < 0.22 ? 0.22 : pull;
          dist = cur.dist * pull;
          px = cur.fx - ox * dist; pz = cur.fz - oz * dist;
          py = cur.fy + cur.height - sp * dist;
        }
      }
      if (!(auto && !cinematic)) pullS.v = 1;
      rig.stats.boomPull = pull;
      rig.stats.cinematic = !!cine.name;

      /* ---- impact springs (real time — must survive hitstop) ------ *
       * Integrated IMPLICITLY and sub-stepped at 60 Hz. The explicit form this
       * used to run —  v += (-K*x - C*v)*rdt;  x += v*rdt  — is only
       * conditionally stable: the velocity update multiplies by (1 - C*rdt),
       * which flips sign and grows for any frame longer than 2/C = 105 ms, and
       * the position update adds its own limit at 2/sqrt(K) = 163 ms. Every
       * capture scenario advances at 1/12 or 1/10 s (and a real 10 fps frame
       * does the same), so the dolly spring detonated at ~1.9x per frame with
       * the sign alternating — which threw the camera clean through the pivot
       * and left `lookAt` aiming back up the boom at the sky. See
       * docs/REQUESTS.md, "the follow camera flips to pointing at the SKY".
       *
       * Implicit Euler is A-stable. Solving
       *     v1 = v0 + h*(-K*(x0 + h*v1) - C*v1)
       * gives  v1 = (v0 - h*K*x0) / (1 + h*C + h*h*K), whose denominator is
       * > 1 for every h > 0, so the spring can only ever lose energy. The
       * sub-stepping is not needed for stability — it is there so a 1/12 s
       * capture frame shows the same punch shape a 1/60 s play frame does.
       */
      if (kick.x || kick.z || kick.d || kick.vx || kick.vz || kick.vd || fovp.v || fovp.dv) {
        const n = clamp(Math.ceil(rdt * 60), 1, SPRING_SUB_MAX);
        const h = rdt / n;
        const kden = 1 / (1 + KICK_C * h + KICK_K * h * h);
        const fden = 1 / (1 + FOV_C * h + FOV_K * h * h);
        for (let i = 0; i < n; i++) {
          kick.vx = (kick.vx - KICK_K * kick.x * h) * kden;
          kick.vz = (kick.vz - KICK_K * kick.z * h) * kden;
          kick.vd = (kick.vd - KICK_K * kick.d * h) * kden;
          kick.x += kick.vx * h; kick.z += kick.vz * h; kick.d += kick.vd * h;
          fovp.dv = (fovp.dv - FOV_K * fovp.v * h) * fden;
          fovp.v += fovp.dv * h;
        }
        // park the springs exactly on zero once they are below a millimetre /
        // a hundredth of a degree, so the loop above costs nothing at rest
        if (Math.abs(kick.x) + Math.abs(kick.z) + Math.abs(kick.d) +
            Math.abs(kick.vx) + Math.abs(kick.vz) + Math.abs(kick.vd) < 1e-3) {
          kick.x = kick.z = kick.d = kick.vx = kick.vz = kick.vd = 0;
        }
        if (Math.abs(fovp.v) + Math.abs(fovp.dv) < 1e-3) { fovp.v = fovp.dv = 0; }
      }

      /* ---- apply the kick, but never through the pivot ------------- *
       * The dolly punch shortens the boom, so it moves along +offset. A boom
       * that reaches zero puts the lens ON the hero; a boom that goes negative
       * puts it in FRONT of him with `lookAt` pointing back the way it came —
       * i.e. at the sky. The spring above can no longer produce that, but the
       * invariant is cheap and it is the one thing in this file whose failure
       * costs the player the whole battle, so it is enforced here as well.
       */
      const kdMax = dist - KICK_BOOM_MIN;
      const kd = clamp(kick.d, -dist, kdMax > 0 ? kdMax : 0);
      let kx = kick.x, kz = kick.z;
      const klMax = 0.45 * dist, kl2 = kx * kx + kz * kz;
      if (kl2 > klMax * klMax) { const s = klMax / Math.sqrt(kl2); kx *= s; kz *= s; }
      px += kx + ox * kd;
      pz += kz + oz * kd;
      py += sp * kd;
      rig.stats.boomEff = dist - kd;

      /* ---- hard ground floor (scaffold parity: always on) --------- *
       * After the kick, not before it: `py += sp * kd` pushes the lens DOWN
       * (sp is negative for a look-down rig), so a floor applied before the
       * punch does not stop a heavy blow burying the camera in the hillside.
       */
      const gh = (w.terrain ? w.terrain.heightAt(px, pz) : 0) + GROUND_CLEAR;
      if (py < gh) py = gh;

      /* ---- last-ditch: never hand the renderer a NaN -------------- */
      if (!Number.isFinite(px + py + pz)) {
        px = cur.fx - ox * cur.dist; pz = cur.fz - oz * cur.dist;
        py = cur.fy + cur.height - sp * cur.dist;
        kick.x = kick.z = kick.d = kick.vx = kick.vz = kick.vd = 0;
        fovp.v = fovp.dv = 0;
      }

      /* ---- place, then shake -------------------------------------- */
      _look.set(cur.fx, cur.fy, cur.fz);
      cam.up.set(0, 1, 0);
      cam.position.set(px, py, pz);
      cam.lookAt(_look);

      let roll = 0;
      sk.gap += rdt;
      if (sk.t > 0) {
        sk.t -= rdt;
        const env = clamp01(sk.t / sk.dur);
        // A 22 Hz vibration cannot be shown on a 10 Hz frame: sampled past
        // Nyquist the phase step exceeds pi and the shake aliases into
        // full-amplitude noise, which is what made low-dt captures jitter.
        // Fade it out instead of showing a lie.
        const nyq = clamp01(1 / (2 * sk.freq * rdt));
        const k = sk.amp * env * env * SHAKE_TRANS * (nyq < 1 ? nyq : 1);
        sk.ph += rdt * sk.freq * TAU;
        const s1 = Math.sin(sk.ph), s2 = Math.sin(sk.ph * 1.63 + 1.1), s3 = Math.sin(sk.ph * 1.37 + 2.3);
        cam.getWorldDirection(_fwd);
        _right.set(-_fwd.z, 0, _fwd.x).normalize();
        _up.crossVectors(_right, _fwd).normalize();
        const rx = s1 * k, ry = s2 * k * 0.72;
        cam.position.x += _right.x * rx + _up.x * ry;
        cam.position.y += _right.y * rx + _up.y * ry;
        cam.position.z += _right.z * rx + _up.z * ry;
        if (sk.dirAmp > 0.01) {
          const dk = s1 * k * 0.55 * sk.dirAmp;
          cam.position.x += sk.dx * dk; cam.position.z += sk.dz * dk;
        }
        roll = s3 * sk.amp * env * env * SHAKE_ROLL * (nyq < 1 ? nyq : 1);
        cam.lookAt(_look);
        if (sk.t <= 0) { sk.amp = 0; sk.t = 0; sk.dirAmp = 0; }
      }
      if (roll) cam.rotateZ(roll);

      /* ---- fov ----------------------------------------------------- */
      const wantFov = clamp(cur.fov + fovp.v, 16, 110);
      if (Math.abs(cam.fov - wantFov) > 1e-3) { cam.fov = wantFov; cam.updateProjectionMatrix(); }

      /* ---- reticle hook for the HUD -------------------------------- */
      // The renderer only refreshes matrixWorldInverse during render(), so a
      // projection taken here would be one frame stale. The HUD draws the
      // reticle in ITS lateUpdate off the numbers below, so refresh now.
      cam.updateMatrixWorld(true);
      cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
      const R = rig.reticle;
      if (lock.id >= 0 && lock.weight > 0.02) {
        const t = lock.id;
        const hh = (e.height[t] > 0.1 ? e.height[t] : 1.75) * (e.scale[t] || 1);
        _proj.set(e.x[t], e.y[t] + hh * 0.62, e.z[t]).project(cam);
        const sz = w.engine ? w.engine.size : null;
        R.active = true; R.id = t;
        R.ndcX = _proj.x; R.ndcY = _proj.y;
        R.x = sz ? (_proj.x * 0.5 + 0.5) * sz.w : _proj.x * 0.5 + 0.5;
        R.y = sz ? (0.5 - _proj.y * 0.5) * sz.h : 0.5 - _proj.y * 0.5;
        R.dist = Math.hypot(e.x[t] - cam.position.x, e.z[t] - cam.position.z);
        R.onScreen = _proj.z < 1 && Math.abs(_proj.x) <= 1 && Math.abs(_proj.y) <= 1;
        R.radius = clamp(560 / Math.max(2, R.dist), 14, 90);
      } else if (R.active) { R.active = false; R.id = -1; R.onScreen = false; }

      /* ---- who is standing in the way ----------------------------- */
      if ((frame & 1) === 0) refreshOccluders(w, cam);

      /* ---- stats + bookkeeping ------------------------------------ */
      cam.getWorldDirection(_fwd);
      rig.stats.lookDownDeg = -Math.asin(clamp(_fwd.y, -1, 1)) * 180 / Math.PI;
      rig.stats.crowdN = crowd.raw;
      rig.stats.mode = rig.mode;
      rig.stats.shake = sk.dur > 0 ? sk.amp * clamp01(sk.t / sk.dur) : 0;
      rig.stats.kick = Math.hypot(kick.x, kick.z, kick.d);
      rig.stats.fovPulse = fovp.v;
      rig.stats.kickD = kick.d; rig.stats.kickX = kick.x; rig.stats.kickZ = kick.z;
      rig.stats.fovPulse = fovp.v; rig.stats.rdt = rdt;
      _pos.copy(cam.position);
      storeSeen();
    },

    dispose() { world = null; },
  };
}
